import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';
import { Drawer, Input, Loader, Text } from '@kula-ai/components';
import { ClockRewind, SearchSm } from '@kula-ai/icons-core';
import type { ChatSession } from '@/types/mission-control';
import { useSessionsQuery } from '../../api';
import { agentForgeThreadPath } from '../../constants';
import { formatHistoryTimestamp, stripMentionTags } from '../../helpers';
import { useBarRouteVisibility } from '../../hooks';
import { useAgentForgeStore } from '../../stores';
import type { HistoryPanelProps, HistoryRowProps } from './HistoryPanel.types';

const EMPTY_HINT = 'No chats yet. Start a new conversation.';
const NO_MATCH_HINT = 'No chats match this search.';

const sortByMostRecent = (sessions: ChatSession[]): ChatSession[] =>
  [...sessions].sort((a, b) => {
    const aMs = new Date(a.updated_at || a.created_at).getTime();
    const bMs = new Date(b.updated_at || b.created_at).getTime();
    return bMs - aMs;
  });

const matchesQuery = (session: ChatSession, query: string): boolean => {
  if (!query) return true;
  return stripMentionTags(session.title)
    .toLowerCase()
    .includes(query.toLowerCase());
};

const HistoryRow = ({ session, onSelect }: HistoryRowProps) => {
  const displayTitle = stripMentionTags(session.title) || 'Untitled chat';
  return (
    <Flex
      as="button"
      type="button"
      align="center"
      justify="space-between"
      p="12px"
      borderBottom="1px solid"
      borderColor="border-color"
      bg="transparent"
      textAlign="left"
      w="100%"
      onClick={() => onSelect?.(session)}
      _hover={{ bg: 'secondary-50' }}
      _focusVisible={{
        outline: '2px solid',
        outlineColor: 'primary-300',
        outlineOffset: '-2px',
      }}
      aria-label={displayTitle}
    >
      <Text
        variant="small"
        truncate
        className="text-secondary-900 font-medium text-[14px]"
      >
        {displayTitle}
      </Text>
      <Text variant="small" className="text-secondary-400 ml-3 shrink-0">
        {formatHistoryTimestamp(session.updated_at || session.created_at)}
      </Text>
    </Flex>
  );
};

export const HistoryPanel = ({
  sessions: sessionsProp,
  onSelect,
}: HistoryPanelProps) => {
  const visible = useBarRouteVisibility();
  const historyOpen = useAgentForgeStore((s) => s.historyOpen);
  const toggleHistory = useAgentForgeStore((s) => s.toggleHistory);
  const openTab = useAgentForgeStore((s) => s.openTab);
  const history = useHistory();
  const [query, setQuery] = useState('');

  // Pull sessions from the API by default. Tests can still pass `sessions`
  // directly to bypass the network. Query only runs while the drawer is open
  // so we don't keep a stale fetch warm on every page.
  const fetched = useSessionsQuery({ enabled: historyOpen && !sessionsProp });
  const sessions = useMemo(
    () => sessionsProp ?? fetched.data ?? [],
    [sessionsProp, fetched.data]
  );
  // Show shimmer while the network fetch is in-flight and we have nothing
  // cached yet. Tests pass `sessionsProp` directly, which short-circuits
  // the query — so loading only shows when the live fetch is the source.
  const isLoading = !sessionsProp && fetched.isLoading;

  const visibleSessions = useMemo(() => {
    const sorted = sortByMostRecent(sessions);
    const newSorted = sorted.filter(
      (s: any) => matchesQuery(s, query) && (s.message_count || 0) > 0
    );
    return newSorted;
  }, [sessions, query]);

  // Visibility-gate before mounting the drawer at all — keeps the portal
  // off the DOM on routes where the chat surface isn't allowed.
  if (!visible) return null;

  const handleClose = () => toggleHistory(false);

  const handleSelect = (session: ChatSession) => {
    if (onSelect) {
      onSelect(session);
    } else {
      // Open the tab with the session metadata so the chat header has a
      // real title + createdAt (without this, ThreadContainer falls back
      // to "New chat" and no timestamp because no tab exists yet).
      openTab({
        sessionId: session.id,
        title: session.title || 'Untitled chat',
        status: 'idle',
        createdAt: session.created_at,
      });
      // Navigate to the session's deep-link route — `index.tsx`'s URL→store
      // sync also sets `activeSessionId`, but `openTab` already did so.
      history.push(agentForgeThreadPath(session.id));
    }
    handleClose();
  };

  return (
    <Drawer
      open={historyOpen}
      onOpenChange={(open) => toggleHistory(open)}
      side="right"
      size="500px"
      closeOnOverlayClick
      closeOnEscape
      bodyClassName="!p-0 min-w-[400px] max-w-[500px]"
      title={
        <Flex
          padding={'2px 0px'}
          align="center"
          gap="8px"
          color="secondary-700"
        >
          <ClockRewind
            size={16}
            color="currentColor"
            className="[&_path]:!stroke-[1.5]"
          />
          <Text variant="h3">Chat history</Text>
        </Flex>
      }
    >
      <Box
        data-test-id="agentforge-history"
        display="flex"
        flexDirection="column"
        h="100%"
      >
        <Box
          p="12px"
          borderBottom="1px solid"
          borderBottomColor="border-color"
          position="sticky"
          bg="#fff"
          top="0"
        >
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            leftElement={<SearchSm size={14} color="currentColor" />}
            aria-label="Search chats"
          />
        </Box>
        <Box flex="1" minH="0">
          {isLoading ? (
            <Flex
              align="center"
              justify="center"
              h="100%"
              data-test-id="agentforge-history-loading"
            >
              <Loader size={16} />
            </Flex>
          ) : sessions.length === 0 ? (
            <Box p="12px">
              <Text variant="small" className="text-secondary-400">
                {EMPTY_HINT}
              </Text>
            </Box>
          ) : visibleSessions.length === 0 ? (
            <Box p="12px">
              <Text variant="small" className="text-secondary-400">
                {NO_MATCH_HINT}
              </Text>
            </Box>
          ) : (
            <Flex direction="column">
              {visibleSessions.map((session) => (
                <HistoryRow
                  key={session.id}
                  session={session}
                  onSelect={handleSelect}
                />
              ))}
            </Flex>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};
